import React, { useEffect, useMemo, useRef, useState } from 'react';
import PhonePreview from './Onboarding/PhonePreview/PhonePreview';
import iphoneFrame from '../images/iphone-15-pro-blue-titanium.svg';

const DEFAULT_ASPECT = '1294/2656';
// Serve directly from the public folder (Firebase Hosting CDN)
const DEFAULT_VIDEO_PATH = '/media/cover-360-web.mp4';
const DEFAULT_POSTER_PATH = '/media/cover-360-frame.jpg';

// Resource cache mimicking the original working pipeline (object URLs)
const videoResourceCache = new Map(); // path -> { storageUrl: string|null, objectUrl: string|null, ready: boolean, loadPromise: Promise|null }

function getVideoResource(path) {
  if (!path) return null;
  if (!videoResourceCache.has(path)) {
    videoResourceCache.set(path, {
      storageUrl: null,
      objectUrl: null,
      ready: false,
      loadPromise: null,
    });
  }
  return videoResourceCache.get(path);
}

function buildWrapperClassName(className) {
  return ['relative', className].filter(Boolean).join(' ').trim();
}

async function fetchVideoBlob(url) {
  const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
  if (!response.ok) throw new Error(`Failed to fetch video blob: ${response.status}`);
  return response.blob();
}

async function loadVideoResource(path) {
  const resource = getVideoResource(path);
  if (!resource) return null;
  if (resource.ready && resource.objectUrl) return resource;
  if (resource.loadPromise) return resource.loadPromise;

  resource.loadPromise = (async () => {
    // For public hosting, the path is already a fetchable URL
    if (!resource.storageUrl) {
      resource.storageUrl = path;
    }

    if (!resource.objectUrl) {
      const blob = await fetchVideoBlob(resource.storageUrl);
      resource.objectUrl = URL.createObjectURL(blob);
    }

    await ensureVideoPlayable(resource.objectUrl);
    resource.ready = true;
    resource.loadPromise = null;
    return resource;
  })().catch((error) => {
    resource.loadPromise = null;
    throw error;
  });

  return resource.loadPromise;
}

function ensureVideoPlayable(objectUrl) {
  if (typeof document === 'undefined') {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const probe = document.createElement('video');
    probe.preload = 'auto';
    probe.muted = true;
    probe.playsInline = true;
    probe.setAttribute('playsinline', '');
    probe.setAttribute('webkit-playsinline', '');
    probe.src = objectUrl;

    const finalize = () => {
      probe.removeEventListener('loadeddata', handleReady);
      probe.removeEventListener('canplaythrough', handleReady);
      probe.removeEventListener('error', handleReady);
      probe.src = '';
      resolve(true);
    };

    const handleReady = () => finalize();

    probe.addEventListener('loadeddata', handleReady, { once: true });
    probe.addEventListener('canplaythrough', handleReady, { once: true });
    probe.addEventListener('error', handleReady, { once: true });

    try {
      probe.load();
    } catch (_) {
      finalize();
    }
  });
}

export function isIphoneVideoReady(path = DEFAULT_VIDEO_PATH) {
  const resource = getVideoResource(path);
  return Boolean(resource && resource.ready && resource.objectUrl);
}

export const DEFAULT_IPHONE_VIDEO_PATH = DEFAULT_VIDEO_PATH;
export const DEFAULT_IPHONE_POSTER_PATH = DEFAULT_POSTER_PATH;

export default function IphoneVideo({
  frameSrc = iphoneFrame,
  videoMp4: videoMp4Prop,
  poster: posterProp,
  videoPath = DEFAULT_VIDEO_PATH,
  posterPath = DEFAULT_POSTER_PATH,
  className = 'block h-full md:h-auto w-auto mx-auto',
  onMediaLoaded,
  onMediaError,
  ...rest
}) {
  const { aspectRatio = DEFAULT_ASPECT, style: previewStyle, ...phonePreviewProps } = rest;

  const [videoUrl, setVideoUrl] = useState(() => {
    if (videoMp4Prop) return videoMp4Prop;
    const resource = getVideoResource(videoPath);
    return resource?.objectUrl ?? null;
  });
  const [posterUrl, setPosterUrl] = useState(() => {
    if (posterProp) return posterProp;
    return posterPath || null;
  });
  const [isReady, setIsReady] = useState(() => {
    if (videoMp4Prop) return true;
    return isIphoneVideoReady(videoPath);
  });

  const hasReportedReadyRef = useRef(false);
  const onMediaLoadedRef = useRef(onMediaLoaded);
  const onMediaErrorRef = useRef(onMediaError);

  useEffect(() => {
    onMediaLoadedRef.current = onMediaLoaded;
  }, [onMediaLoaded]);

  useEffect(() => {
    onMediaErrorRef.current = onMediaError;
  }, [onMediaError]);

  useEffect(() => {
    hasReportedReadyRef.current = false;
  }, [videoPath, videoMp4Prop]);

  useEffect(() => {
    let cancelled = false;

    if (videoMp4Prop) {
      setVideoUrl(videoMp4Prop);
      setIsReady(true);
      return () => {
        cancelled = true;
      };
    }

    const resource = getVideoResource(videoPath);

    if (resource?.objectUrl) {
      setVideoUrl(resource.objectUrl);
      if (resource.ready) {
        setIsReady(true);
        return () => {
          cancelled = true;
        };
      }
      setIsReady(false);
    } else {
      setIsReady(false);
    }

    loadVideoResource(videoPath)
      .then((res) => {
        if (cancelled) return;
        setVideoUrl(res.objectUrl);
        setIsReady(true);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(`[IphoneVideo] Failed to prepare video for ${videoPath}`, error);
        if (onMediaErrorRef.current) {
          try {
            onMediaErrorRef.current(error);
          } catch (_) {
            // ignore callback errors
          }
        }
        setIsReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [videoMp4Prop, videoPath]);

  useEffect(() => {
    // Poster now comes directly from public path or explicit prop (no async storage lookups)
    if (posterProp) {
      setPosterUrl(posterProp);
    } else {
      setPosterUrl(posterPath || null);
    }
  }, [posterProp, posterPath]);

  useEffect(() => {
    if (isReady && videoUrl && !hasReportedReadyRef.current) {
      hasReportedReadyRef.current = true;
      if (onMediaLoadedRef.current) {
        try {
          onMediaLoadedRef.current({ videoUrl, posterUrl: posterUrl ?? null });
        } catch (_) {
          // ignore callback errors
        }
      }
    }
  }, [isReady, videoUrl, posterUrl]);

  const handleVideoReady = () => {
    const resource = getVideoResource(videoPath);
    if (resource) {
      resource.ready = true;
    }
    setIsReady(true);
  };

  const handleVideoError = (event) => {
    if (onMediaErrorRef.current) {
      try {
        onMediaErrorRef.current(event);
      } catch (_) {
        // ignore callback errors
      }
    }
    setIsReady(true);
  };

  const wrapperClassName = buildWrapperClassName(className);
  // Keep PhonePreview instance stable across blob URL changes to avoid remount resets
  const previewKey = useMemo(
    () => (videoPath ? `iphone-video-${videoPath}` : 'iphone-video'),
    [videoPath],
  );
  const previewStyleWithVisibility = useMemo(
    () => ({
      ...(previewStyle || {}),
      // Keep the video visible to avoid decoder restarts on visibility flips; rely on overlay skeleton.
    }),
    [previewStyle],
  );

  return (
    <div className={wrapperClassName}>
      {!isReady && (
        <div className="absolute inset-0 rounded-[28px] bg-gray-200 animate-pulse" />
      )}
      <PhonePreview
        key={previewKey}
        frameSrc={frameSrc}
        videoMp4={videoUrl || undefined}
        poster={posterUrl || undefined}
        className="w-full h-full"
        aspectRatio={aspectRatio}
        style={previewStyleWithVisibility}
        onVideoReady={handleVideoReady}
        onVideoError={handleVideoError}
        {...phonePreviewProps}
      />
    </div>
  );
}
